import type { CSSProperties, FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FaqList } from '@/components/generals/faq-list'
import { OpenStatus } from '@/components/generals/open-status'
import { SplitWords } from '@/components/generals/split-words'
import { SectionLabel } from '@/components/generals/section-label'
import { PHOTO } from '@/constants/media'
import { CONTACT, LINKS } from '@/constants/site'

type Channel = {
    verb: string
    value: string
    href: string
    text: string
    internal?: boolean
    external?: boolean
}

const CHANNELS: Channel[] = [
    {
        verb: 'Ring oss',
        value: CONTACT.phone,
        href: CONTACT.phoneHref,
        text: 'For deg som ønsker en god, gammel telefonsamtale.'
    },
    {
        verb: 'Skriv til oss',
        value: CONTACT.email,
        href: `mailto:${CONTACT.email}`,
        text: 'Perfekt for å komme i kontakt utenfor åpningstiden vår.'
    },
    {
        verb: 'Bli oppringt',
        value: 'Vi ringer deg',
        href: '/bli-oppringt',
        text: 'Legg igjen nummeret, så tar vi telefonen.',
        internal: true
    },
    {
        verb: 'Hjelpesenteret',
        value: 'Svar på det meste',
        href: LINKS.help,
        text: 'Steg-for-steg-guider til alt i Svenn.',
        external: true
    }
]

const FAQ: Faq[] = [
    {
        question: 'Hvordan bytter jeg passord i Svenn?',
        answer: [
            "Gå til siden for å tilbakestille passord, skriv inn e-postadressen din og trykk «Send lenke for å tilbakestille passord». Har du ikke fått e-posten innen 5 minutter, sjekk søppelposten."
        ],
        link: { label: 'Tilbakestill passordet', href: LINKS.forgotPassword }
    },
    {
        question: 'Hvilke språk støtter Svenn?',
        answer: [
            "Svenn finnes på ni språk: norsk, engelsk, dansk, polsk, tysk, litauisk, latvisk, russisk og serbisk."
        ]
    },
    {
        question: 'Hvordan endrer jeg språk i Svenn?',
        answer: [
            "Språket velges når en ansatt opprettes, og både administrator og ansatt kan endre det senere. Å endre språk på nett endrer ikke språket i mobilappen.",
            "På nett åpner du profilen din nederst til venstre og velger språk i listen. På iPhone går du til Innstillinger, så Svenn-appen, og velger foretrukket språk. På Android følger appen språket på telefonen, som du vanligvis endrer under Innstillinger, Generelt, Språk."
        ]
    },
    {
        question: 'Hvordan synkroniserer jeg Svenn-appen?',
        answer: [
            "Appen fungerer uten dekning, så noen ganger må du synkronisere manuelt. Åpne menyen og trykk «Synkroniser nå»."
        ]
    },
    {
        question: 'Hvor finner jeg hjelpesenteret?',
        answer: [
            "Hjelpesenteret ligger på service.svenn.com/help, med guider til alle delene av Svenn."
        ],
        link: { label: 'Åpne hjelpesenteret', href: LINKS.help }
    }
]

const ChannelRow: FC<{ channel: Channel, index: number }> = ({ channel, index }): ReactNode => {
    const content = (
        <>
            <span className='t-label col-span-12 text-tone-muted md:col-span-2'>{channel.verb}</span>
            <span className='contact-value col-span-11 md:col-span-6'>{channel.value}</span>
            <span className='col-span-12 text-tone-muted md:col-span-3 md:pt-2'>{channel.text}</span>
            <ArrowUpRight className='contact-arrow col-span-1 size-6 justify-self-end md:size-8' aria-hidden='true' />
        </>
    )

    const className = 'contact-row grid grid-cols-12 items-start gap-x-6 gap-y-2 border-b border-tone-line py-8 md:py-10'
    const style = { '--d': index } as CSSProperties

    if (channel.internal) {
        return (
            <li data-reveal='rise' style={style}>
                <Link href={channel.href} className={className}>{content}</Link>
            </li>
        )
    }

    return (
        <li data-reveal='rise' style={style}>
            <a href={channel.href} className={className} {...(channel.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {content}
                {channel.external && <span className='sr-only'> (åpnes i ny fane)</span>}
            </a>
        </li>
    )
}

const Kontakt: FC = (): ReactNode => {
    return (
        <Layout header='light'>
            <Seo
                title='Kontakt'
                description="Ta kontakt med Svenn. Ekte folk på telefon 95 45 67 40 hverdager 08 til 16, eller skriv til support@svenn.com. Skippergata 9, 0152 Oslo."
            />

            <section id='kontakt' className='tone-paper relative overflow-hidden' data-header='light'>
                <div className='grid-lines' />

                <div className='wrap relative pt-[calc(var(--header-h)+5rem)] pb-16 md:pb-24'>
                    <p className='t-label load-fade text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>Kontakt · Skippergata 9, Oslo</p>

                    <h1 className='t-mega mt-8 balance'>
                        <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                            <SplitWords text='Snakk med' />
                        </span>
                        <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.3s' } as CSSProperties}>
                            <SplitWords text='ekte folk.' start={2} />
                        </span>
                    </h1>

                    <div className='load-fade mt-12 grid grid-cols-12 gap-x-6 gap-y-6' style={{ '--load-delay': '0.7s' } as CSSProperties}>
                        <p className='t-lead col-span-12 max-w-xl text-tone-muted pretty md:col-span-6'>
                            Ingen roboter her i gården. Snakk med ekte folk, mandag til fredag mellom 08 og 16.
                        </p>
                        <div className='col-span-12 md:col-span-5 md:col-start-8 md:justify-self-end'>
                            <OpenStatus />
                        </div>
                    </div>

                    <ul className='mt-16 border-t border-tone-fg md:mt-24'>
                        {CHANNELS.map((channel, i) => (
                            <ChannelRow key={channel.verb} channel={channel} index={i} />
                        ))}
                    </ul>
                </div>
            </section>

            <section id='besok-oss' className='tone-night grain relative overflow-hidden py-24 md:py-36' data-header='dark'>
                <div className='wrap grid grid-cols-12 gap-x-6 gap-y-12'>
                    <div className='col-span-12 lg:col-span-5'>
                        <SectionLabel index='02'>Besøk oss</SectionLabel>
                        <p className='t-h1 mt-6'>
                            {CONTACT.street}
                            <span className='t-serif block text-tone-ink'>{CONTACT.city}</span>
                        </p>
                        <p className='mt-6 max-w-sm text-tone-muted'>{CONTACT.hours}. Ta gjerne kontakt før du kommer innom, så sørger vi for at noen er der.</p>
                        <a href={CONTACT.mapsHref} target='_blank' rel='noopener noreferrer' className='u-link-static mt-8 inline-flex items-center gap-2 font-semibold'>
                            Åpne i kart
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                            <span className='sr-only'> (åpnes i ny fane)</span>
                        </a>
                    </div>

                    <figure className='col-span-12 lg:col-span-6 lg:col-start-7'>
                        <div className='aspect-[4/3] overflow-hidden bg-night-raised' data-reveal='frame'>
                            <img src={PHOTO.svennTeam} alt='To medarbeidere fra Svenn i et møte på kontoret i Oslo' loading='lazy' decoding='async' className='size-full object-cover' />
                        </div>
                        <figcaption className='t-label mt-4 text-tone-muted'>Fra kontoret i Skippergata</figcaption>
                    </figure>
                </div>
            </section>

            <FaqList items={FAQ} index='03' />
        </Layout>
    )
}

export default Kontakt
